import { Component, ElementRef, EventEmitter, inject, Input, NgZone, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faExclamationCircle,
  faExclamationTriangle,
  faInfoCircle,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons";
import { CommonModule } from "@angular/common";
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TooltipModule } from '@cloudfactorydk/ng2-tooltip-directive';
import { MatCheckbox } from "@angular/material/checkbox";
import { FormsModule } from "@angular/forms";
import { faCarBuilding } from "@fortawesome/pro-duotone-svg-icons";
import type { BarData, BarOption, BarTooltip, CapacityView, Deadline } from "./capacity-bars.model";
import type { TwoWeekGridData } from "./two-week-grid.util";
import {
  absenceCellCode,
  buildGridRows,
  formatShortDate,
  GridViewModel,
  isoWeek,
  startOfWeek,
} from "./two-week-grid.util";

interface Label {
  color: string,
  description: string
}

interface UnitAmountSteps {
  percentage: number,
  description: string
}

// Segment innerhalb einer Balken-Spalte: Position/Höhe in Prozent,
// auf den sichtbaren Bereich (unitAmountStart - unitAmountEnd) geclamped
interface BarSegment {
  bottom_percent: number
  height_percent: number
  icon?: IconDefinition,
  tooltip: BarTooltip,
  color: string,
  style?: 'filled' | 'bordered'
}

interface Bar {
  description: string;
  segments: BarSegment[];
  data: BarData;
}

@Component({
  imports: [CommonModule, FontAwesomeModule, TooltipModule, MatCheckbox, FormsModule],
  selector: 'hawk-capacity-bars',
  templateUrl: './capacity-bars.component.html',
  styleUrls: ['./capacity-bars.component.scss', './../../_components/_shared/styles/common-styles.scss'],
  standalone: true
})
export class CapacityBarsComponent implements OnChanges {

  private ngZone = inject(NgZone)

  @Input() barData: BarData[]
  @Input() measureUnit: string = '%'
  @Input() deadlines: Deadline[]
  @Input() labels: Label[]

  @Input() unitAmountStart: number = 0
  @Input() unitAmountEnd: number = 100
  @Input() unitAmountInterSteps: number = 1

  @Input() filter: string[] = []
  @Input() options: BarOption[] = []

  // Ansicht: Tages-Balken ('day') oder 2-Wochen-Grid ('grid')
  @Input() view: CapacityView = 'day'
  // Referenzdatum für Perioden-Label und Navigation (selektiertes Dashboard-Datum)
  @Input() periodDate?: Date
  // Daten der 2-Wochen-Ansicht (von der Eltern-Komponente geladen)
  @Input() gridData?: TwoWeekGridData

  @Output() clickBarEvent = new EventEmitter<number>()
  @Output() changeOptionEvent = new EventEmitter<{idx: number, option: string, new_value: boolean}>()
  @Output() viewChangeEvent = new EventEmitter<CapacityView>()
  @Output() navigateEvent = new EventEmitter<'prev' | 'next' | 'today'>()

  public unitAmountSteps: UnitAmountSteps[] = []
  public bars: Bar[] = []
  public activeFilter: string = ''

  // Optionen-Bereich auf-/zugeklappt (Checkboxen über den Balken-Spalten)
  public optionsExpanded = false

  // Aufbereitete Daten der 2-Wochen-Ansicht für das Template
  public grid: GridViewModel = {
    rows: [], techs: [], footer: [],
    team: { value: null, label: '—', title: '', bg: '#e5e7eb', fg: '#6b7280' },
  }

  public icons = {
    loading_error: faExclamationCircle,
    finished: faCheck,
    home_travel: faCarBuilding
  }

  // Nur die Balken, die dem aktiven Filter entsprechen
  get shownBars(): Bar[] {
    return this.bars.filter(bar => this.barIsShown(bar))
  }

  // Label der Werkzeugleiste: Wochentag + Datum (Tag) bzw. KW-Spanne (2 Wochen)
  get periodLabel(): string {
    const date = this.periodDate ?? new Date()
    if (this.view == 'grid') {
      const monday = startOfWeek(date)
      const second_friday = new Date(monday)
      second_friday.setDate(second_friday.getDate() + 11)
      return `KW ${isoWeek(monday)}–${isoWeek(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 7))} · ${formatShortDate(monday)} – ${formatShortDate(second_friday)}`
    }
    const weekdays_long = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag']
    return `${weekdays_long[date.getDay()]}, ${formatShortDate(date, true)}`
  }

  // True, wenn das Referenzdatum in der aktuellen Periode liegt (Heute-Button deaktiv)
  get isCurrentPeriod(): boolean {
    const date = this.periodDate ?? new Date()
    const today = new Date()
    if (this.view == 'grid') {
      return startOfWeek(date).toDateString() == startOfWeek(today).toDateString()
    }
    return date.toDateString() == today.toDateString()
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.updateUnitAmountSteps()
    this.updateBarSegments()
    this.grid = this.gridData
      ? buildGridRows(this.gridData, new Date())
      : { rows: [], techs: [], footer: [], team: { value: null, label: '—', title: '', bg: '#e5e7eb', fg: '#6b7280' } }
  }

  setView(view: CapacityView): void {
    this.viewChangeEvent.emit(view)
  }

  barIsShown(bar: Bar): boolean {
    if (!this.activeFilter) {
      return true
    } else {
      return !!bar.data.filter_attributes.find(attr => attr == this.activeFilter)
    }
  }

  // Stundenlabels der linken Achse (Position in Prozent von unten)
  private updateUnitAmountSteps() {
    const res: UnitAmountSteps[] = []
    const range = this.unitAmountEnd - this.unitAmountStart
    // Schrittgröße aus dem sichtbaren Bereich berechnen (z.B. 2 Stunden)
    const step_value = range / (this.unitAmountInterSteps + 1)

    // Erstes Label am nächsten Schritt-Raster ab unitAmountStart
    const first_aligned = Math.ceil(this.unitAmountStart / step_value) * step_value

    for (let value = first_aligned; value <= this.unitAmountEnd; value += step_value) {
      const percentage = ((value - this.unitAmountStart) / range) * 100
      res.push({
        percentage: percentage,
        description: `${value}${this.measureUnit}`
      })
    }
    this.unitAmountSteps = res
  }

  private updateBarSegments() {
    const range = this.unitAmountEnd - this.unitAmountStart
    this.bars = this.barData.map(bar_item => {
      return {
        segments: this.barDataItemToBarSegments(bar_item, range),
        description: bar_item.description,
        data: bar_item
      }
    })
  }

  // Rechnet die Werte eines Balkens in Prozent-Segmente um und clamped sie
  // auf den sichtbaren Bereich (0-100), damit nichts aus der Spalte hinausläuft
  private barDataItemToBarSegments(bar_item: BarData, range: number): BarSegment[] {
    const segments: BarSegment[] = []

    let current_percentage = ((bar_item.start_at_unit_amount - this.unitAmountStart) / range) * 100
    for (const value of bar_item.values) {
      const from_percent = Math.max(0, current_percentage)
      current_percentage += (value.unit_amount / range) * 100
      const to_percent = Math.min(100, current_percentage)

      if (to_percent > from_percent) {
        segments.push({
          bottom_percent: from_percent,
          height_percent: to_percent - from_percent,
          icon: value.icon ? this.icons[value.icon] : null,
          tooltip: value.tooltip,
          color: value.color,
          style: value.style
        })
      }
    }

    return segments
  }

  getFilterInstanceAmount(filter: string): number {
    return this.bars.filter(bar => bar.data.filter_attributes.includes(filter)).length
  }

  // Position gestrichelter Linien (z.B. "Jetzt") in Prozent von unten, geclamped auf 0-100
  getDeadlinePercentage(deadline: Deadline): number {
    const range = this.unitAmountEnd - this.unitAmountStart
    return Math.min(100, Math.max(0, ((deadline.at_value - this.unitAmountStart) / range) * 100))
  }

  // CSS-Klasse des Auslastungs-Badges nach Schwellenwerten
  // (>= 100 % voll, >= 80 % hoch, >= 50 % mittel, darunter niedrig)
  getUtilizationClass(percent: number): 'full' | 'high' | 'medium' | 'low' {
    if (percent >= 100) return 'full'
    if (percent >= 80) return 'high'
    if (percent >= 50) return 'medium'
    return 'low'
  }

  // Badge-Text: Auslastung in Prozent oder Gedankenstrich (z.B. bei Abwesenheit)
  getUtilizationLabel(bar: Bar): string {
    return bar.data.utilizationPercent != null ? `${bar.data.utilizationPercent}%` : '—'
  }

  // Kürzel eines Abwesenheitsgrunds (U/K/F/S/A) für den Badge abwesender Techniker
  absenceCode(reason: string): string {
    return absenceCellCode(reason)
  }

  // Namensbereich der Balken-Ansicht: feste Höhe, damit die 45°-schräg
  // laufenden Namen vollständig sichtbar bleiben (1:1 mit .bar_name und
  // .axis_name_spacer in der SCSS pflegen). Die Balken-Fläche selbst ist
  // flexibel (flex: 1) und passt sich der verfügbaren Kachelhöhe an
  readonly barNameHeightPx = 120

  // Feste Höhe des Namensbereichs in der 2-Wochen-Ansicht
  readonly gridNameHeightPx = 100

  // Basis-Schriftgröße der vertikalen Namen (px, 2-Wochen-Ansicht)
  readonly nameFontPx = 13

  // Gemessene Größe des 2-Wochen-Grids (ResizeObserver) — daraus werden
  // Zellbreite/-höhe und Schriftgröße so skaliert, dass alle Techniker ohne
  // Scrollen in die Kachel passen
  gridAvailableWidth = 0
  gridAvailableHeight = 0

  private grid_size_observer?: ResizeObserver

  @ViewChild('gridWrap') set gridWrapRef(element: ElementRef<HTMLDivElement> | undefined) {
    if (typeof ResizeObserver === 'undefined') return
    this.grid_size_observer ??= new ResizeObserver(entries => {
      for (const entry of entries) {
        // ResizeObserver läuft außerhalb der Angular-Zone — die Änderung muss
        // die Change Detection anstoßen, sonst bleibt das Template bei den
        // Startwerten hängen
        this.ngZone.run(() => {
          this.gridAvailableWidth = entry.contentRect.width
          this.gridAvailableHeight = entry.contentRect.height
        })
      }
    })
    this.grid_size_observer.disconnect()
    if (element) {
      this.grid_size_observer.observe(element.nativeElement)
    }
  }

  // Zellbreite des 2-Wochen-Grids: verfügbarer Platz abzüglich Tages- und
  // Ø-Spalte, gleichmäßig auf die Techniker verteilt (22px Minimum, 40px Maximum)
  get gridCellWidth(): number {
    const techs = Math.max(1, this.grid.techs.length)
    const available = Math.max(0, this.gridAvailableWidth - 96 - 78)
    return Math.max(22, Math.min(40, Math.floor(available / techs)))
  }

  // Schriftgröße der Grid-Zellen passend zur Zellbreite
  get gridCellFontPx(): number {
    const width = this.gridCellWidth
    if (width >= 36) return 11
    if (width >= 28) return 10
    return 8.5
  }

  // Tages-Zeilenhöhe: verfügbarer Platz nach KW-Zeilen (2 × 24px), Ø-Zeile
  // (26px) und Namensbereich (100px), gleichmäßig auf die 10 Tage verteilt
  // (12px Minimum, 22px Maximum)
  get gridRowHeight(): number {
    const fixed = 2 * 24 + 26 + 100 + 8
    const available = Math.max(0, this.gridAvailableHeight - fixed)
    return Math.max(12, Math.min(22, Math.floor(available / 10)))
  }

  // Skalierungsfaktor für vertikale Namen: lange Namen schrumpfen leicht,
  // damit sie nicht abgeschnitten werden (1 = Originalgröße)
  getNameScale(description: string, available_px: number = this.gridNameHeightPx): number {
    if (!description.length) return 1
    // geschätzte Textlänge: Zeichenanzahl × Schriftgröße × durchschnittliche Zeichenbreite (em)
    const estimated_px = description.length * this.nameFontPx * 0.62
    return Math.min(1, available_px / estimated_px)
  }

  // Schriftgröße des Namens in px: Skalierung über font-size statt transform,
  // damit alle Namen bündig direkt unter dem Balken beginnen (nur noch
  // 2-Wochen-Ansicht; die Tagesansicht nutzt feste 45°-schräge Namen)
  getNameFontPx(description: string, available_px: number = this.gridNameHeightPx): number {
    return this.nameFontPx * this.getNameScale(description, available_px)
  }

  protected readonly faInfoCircle = faInfoCircle;
  protected readonly faExclamationTriangle = faExclamationTriangle;
  protected readonly faChevronUp = faChevronUp;
  protected readonly faChevronDown = faChevronDown;
}
